import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Save, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function JobManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", company: "", location: "", role_type: "", experience: "", description: "", salary_range: "", is_active: true });
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/jobs").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title || !form.company || !form.location) { toast.error("Title, company and location required"); return; }
    try {
      if (editId) await api.put(`/jobs/${editId}`, form);
      else await api.post("/jobs", form);
      toast.success("Saved"); setOpen(false); setEditId(null); load();
    } catch { toast.error("Failed"); }
  };

  const handleEdit = (item) => {
    setForm({ title: item.title, company: item.company, location: item.location, role_type: item.role_type || "", experience: item.experience || "", description: item.description || "", salary_range: item.salary_range || "", is_active: item.is_active !== false });
    setEditId(item.id); setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/jobs/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="job-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-serif font-bold text-foreground">Jobs</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans" onClick={() => { setForm({ title: "", company: "", location: "", role_type: "", experience: "", description: "", salary_range: "", is_active: true }); setEditId(null); setOpen(true); }} data-testid="new-job-btn">
              <Plus className="h-4 w-4 mr-2" /> New Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif">{editId ? "Edit" : "New"} Job</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div><Label className="font-sans text-sm">Title *</Label><Input value={form.title} onChange={e => set("title", e.target.value)} className="mt-1" data-testid="job-title" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-sans text-sm">Company *</Label><Input value={form.company} onChange={e => set("company", e.target.value)} className="mt-1" data-testid="job-company" /></div>
                <div><Label className="font-sans text-sm">Location *</Label><Input value={form.location} onChange={e => set("location", e.target.value)} className="mt-1" data-testid="job-location" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-sans text-sm">Role Type</Label><Input value={form.role_type} onChange={e => set("role_type", e.target.value)} placeholder="Doctor" className="mt-1" data-testid="job-role" /></div>
                <div><Label className="font-sans text-sm">Experience</Label><Input value={form.experience} onChange={e => set("experience", e.target.value)} placeholder="3-5 years" className="mt-1" /></div>
              </div>
              <div><Label className="font-sans text-sm">Salary Range</Label><Input value={form.salary_range} onChange={e => set("salary_range", e.target.value)} placeholder="$80k - $120k" className="mt-1" data-testid="job-salary" /></div>
              <div><Label className="font-sans text-sm">Description</Label><Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3} className="mt-1" data-testid="job-description" /></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => set("is_active", v)} /><Label className="font-sans text-sm">Active</Label></div>
              <Button onClick={handleSave} className="w-full bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="save-job"><Save className="h-4 w-4 mr-2" />Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="font-sans">Title</TableHead>
            <TableHead className="font-sans">Company</TableHead>
            <TableHead className="font-sans">Location</TableHead>
            <TableHead className="font-sans">Salary</TableHead>
            <TableHead className="font-sans text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(job => (
              <TableRow key={job.id}>
                <TableCell className="font-sans font-medium">{job.title}</TableCell>
                <TableCell className="font-sans text-sm">{job.company}</TableCell>
                <TableCell className="font-sans text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</TableCell>
                <TableCell className="font-sans text-sm text-[#1E88E5]">{job.salary_range}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(job)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDelete(job.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
